﻿import React, { useState, useEffect } from "react";
import { ChevronRight, ArrowLeft } from "lucide-react";
import { SCREEN_SECTIONS, POPULAR_IDS } from "../data/screens.js";
import { INDUSTRIES } from "../data/industries.js";
import BrowseSectors from "./BrowseSectors.jsx";
import IndustryCompanies from "./IndustryCompanies.jsx";
import { api } from "../utils/api.js";
import { track } from "../utils/track.js";
import { loadUserData, saveUserData } from "../utils/userStore.js";

const BUILT_IN = SCREEN_SECTIONS.flatMap((s) => s.screens);

const BASE_COLS = [
  { key: "price", label: "CMP $", unit: "$" },
  { key: "pe", label: "P/E", unit: "x" },
  { key: "mcap", label: "Mar Cap $B", unit: "$B" },
  { key: "div_yield", label: "Div Yld %", unit: "%" },
  { key: "roce", label: "ROCE %", unit: "%" },
];

function fmt(v, unit) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  if (unit === "flag") return n ? "Yes" : "No";
  if (unit === "days") return String(Math.round(n));
  if (unit === "$M") return Math.round(n).toLocaleString("en-US");
  if (unit === "") return Math.abs(n) >= 1000 ? Math.round(n).toLocaleString("en-US") : String(Number(n.toFixed(2)));
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const headLabel = (c) => (["$", "$B", "$M", "%"].includes(c.unit) ? `${c.label} ${c.unit}` : c.label);

function ScreenTile({ screen, onOpen }) {
  return (
    <button className="screen-tile" onClick={() => onOpen(screen)}>
      <span className="screen-tile-title">
        {screen.name} <ChevronRight size={16} />
      </span>
      <span className="screen-tile-desc">{screen.description}</span>
    </button>
  );
}

function ScreenSection({ title, subtitle, screens, onOpen, footer }) {
  return (
    <section className="card screen-section">
      <h2 className="screen-section-title">{title}</h2>
      <p className="screen-section-sub">{subtitle}</p>
      <div className="screen-grid">
        {screens.map((s) => (
          <ScreenTile key={s.id} screen={s} onOpen={onOpen} />
        ))}
      </div>
      {footer}
    </section>
  );
}

/* ---------- Results of one screen ---------- */
function Results({ screen, isCustom, onBack, onSaveQuery, onDelete }) {
  // override === null: run the built-in screen as it is. A string: run that query.
  const [override, setOverride] = useState(isCustom ? screen.query || "" : null);
  const [draft, setDraft] = useState(isCustom ? screen.query || "" : "");
  const [sort, setSort] = useState(null);
  const [limit, setLimit] = useState(100);
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [note, setNote] = useState("");
  useEffect(() => { track("open_screen", { id: screen.id, name: screen.name }); }, []);

  useEffect(() => {
    if (override !== null && !override.trim()) {
      setState({ loading: false, error: "", data: null });
      return undefined;
    }
    let dead = false;
    setState((s) => ({ ...s, loading: true, error: "" }));
    const body = override === null ? { screenId: screen.id, limit } : { query: override, limit };
    if (sort) body.sort = sort;
    api("/screener/run", { method: "POST", body })
      .then((data) => {
        if (dead) return;
        setState({ loading: false, error: "", data });
        if (override === null && data.query) setDraft((d) => d || data.query);
      })
      .catch((e) => {
        if (!dead) setState({ loading: false, error: e.message, data: null });
      });
    return () => {
      dead = true;
    };
  }, [override, sort, limit, screen.id]);

  const { loading, error, data } = state;
  const active = sort || (data && data.sort) || [];

  const run = (e) => {
    e.preventDefault();
    setSort(null);
    setLimit(100);
    setNote("");
    track("run_query", { screen: screen.id, query: draft.trim() });
    setOverride(draft.trim());
  };
  const toggleSort = (key) => {
    setLimit(100);
    setSort([key, active[0] === key ? (active[1] === "desc" ? "asc" : "desc") : key === "name" ? "asc" : "desc"]);
  };
  const arrow = (key) => (active[0] === key ? (active[1] === "asc" ? " ▲" : " ▼") : "");

  const cols = data ? [...BASE_COLS, ...(data.columns || []).map((c) => ({ key: c.key, label: headLabel(c), unit: c.unit }))] : BASE_COLS;
  const rows = data ? data.rows : [];

  return (
    <main className="page-bg">
      <div className="dash-main-content screens-wrap">
        <a href="#" className="go-back mb-3" onClick={(e) => { e.preventDefault(); onBack(); }}>
          <ArrowLeft size={16} /> All screens
        </a>
        <h1 className="page-title">{screen.name}</h1>
        <p className="text-muted mb-3">{screen.description}</p>

        {!(data && data.unavailable) && (
          <form className="card mb-3" style={{ padding: 14 }} onSubmit={run}>
            <label className="small text-muted mb-1" htmlFor="screen-query">Query (edit it and press Run to try your own version)</label>
            <textarea
              id="screen-query"
              rows={2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder='e.g.  pe < 15 AND roce > 20 AND sector = "Technology"'
              style={{ width: "100%", fontFamily: "monospace", fontSize: 14, padding: 8, border: "1px solid #ddd", borderRadius: 6 }}
            />
            <div className="d-flex gap-2 mt-2 align-items-center flex-wrap">
              <button type="submit" className="btn btn-purple" disabled={!draft.trim()}>Run</button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={!draft.trim()}
                onClick={() => setNote(onSaveQuery(draft.trim()))}
              >
                {isCustom ? "Save changes" : "Save as my screen"}
              </button>
              {isCustom && (
                <button type="button" className="btn btn-outline-secondary" onClick={() => { if (window.confirm("Delete this screen?")) onDelete(); }}>
                  Delete screen
                </button>
              )}
              {note && <span className="small text-muted">{note}</span>}
            </div>
          </form>
        )}

        {error && <div className="alert alert-danger">{error}</div>}
        {data && data.unavailable && <div className="alert alert-secondary">{data.note}</div>}
        {isCustom && override !== null && !override.trim() && (
          <div className="alert alert-secondary">This screen has no query yet. Write one above and press Run.</div>
        )}

        {data && !data.unavailable && (
          <div className="card wl-table-card">
            <div className="px-3 pt-3 small text-muted">
              {loading ? "Running..." : `${data.total.toLocaleString("en-US")} companies match`}
              {data.total > rows.length ? ` · showing ${rows.length}` : ""}
            </div>
            <div className="table-responsive">
              <table className="table wl-table mb-0">
                <thead>
                  <tr>
                    <th className="wl-th-plain">S.No.</th>
                    <th className="wl-th-plain" style={{ cursor: "pointer" }} onClick={() => toggleSort("name")}>Company{arrow("name")}</th>
                    {cols.map((c) => (
                      <th key={c.key} className="wl-th-plain text-end" style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort(c.key)}>
                        {c.label}{arrow(c.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.ticker}>
                      <td>{i + 1}.</td>
                      <td>
                        <a className="wl-company" href={`#/stock/${r.ticker}`}>{r.name}</a>
                        <span className="text-muted small ms-2">{r.ticker}</span>
                      </td>
                      {cols.map((c) => (
                        <td key={c.key} className="text-end">{fmt(r[c.key], c.unit)}</td>
                      ))}
                    </tr>
                  ))}
                  {!rows.length && !loading && (
                    <tr><td colSpan={cols.length + 2} className="text-center text-muted py-4">No companies match this query.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {data.total > rows.length && (
              <div className="p-3 text-center">
                <button className="btn btn-outline-secondary" disabled={loading || limit >= 500} onClick={() => setLimit((l) => Math.min(l + 100, 500))}>
                  Show more
                </button>
              </div>
            )}
            <p className="text-muted small wl-note">
              Computed from your own database: prices from Yahoo Finance (15 minutes delayed), financials from SEC filings. Numbers refresh after the market closes.
            </p>
          </div>
        )}
        {loading && !data && !error && <p className="text-muted">Running...</p>}
      </div>
    </main>
  );
}

/* ---------- Create a screen ---------- */
function CreateModal({ onSave, onClose }) {
  const [form, setForm] = useState({ name: "", description: "", query: "" });
  const [fields, setFields] = useState([]);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api("/screener/fields").then(setFields).catch(() => {});
  }, []);

  const groups = fields.reduce((acc, f) => {
    (acc[f.group] = acc[f.group] || []).push(f);
    return acc;
  }, {});
  const addField = (key) => setForm((f) => ({ ...f, query: `${f.query}${f.query && !/\s$/.test(f.query) ? " " : ""}${key} ` }));

  const check = async () => {
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      const r = await api("/screener/run", { method: "POST", body: { query: form.query, limit: 1 } });
      setMsg({ type: "ok", text: `${r.total.toLocaleString("en-US")} companies match right now.` });
      return true;
    } catch (e) {
      setMsg({ type: "err", text: e.message });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.query.trim()) return;
    if (await check()) onSave({ name: form.name.trim(), description: form.description.trim() || "Custom screen", query: form.query.trim() });
  };

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-card" style={{ maxWidth: 640, width: "92vw", maxHeight: "90vh", overflowY: "auto" }}>
        <h3>Create new screen</h3>
        <form onSubmit={submit}>
          <div className="form-group">
            <label>Screen name</label>
            <input type="text" required autoFocus maxLength={60} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Cheap compounders" />
          </div>
          <div className="form-group">
            <label>Description (optional)</label>
            <input type="text" maxLength={120} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What does this screen look for?" />
          </div>
          <div className="form-group">
            <label>Query</label>
            <textarea
              rows={3}
              required
              value={form.query}
              onChange={(e) => setForm({ ...form, query: e.target.value })}
              placeholder='pe < 15 AND roce > 20 AND mcap > 5 AND sector = "Technology"'
              style={{ width: "100%", fontFamily: "monospace", fontSize: 14, padding: 8, border: "1px solid #ddd", borderRadius: 6 }}
            />
            <div className="small text-muted mt-1">
              Compare with &gt; &gt;= &lt; &lt;= = !=. Join with AND / OR and brackets. Text fields use = or ~ (contains), e.g. industry ~ "software".
              Market cap is in $ billions.
            </div>
          </div>
          <details className="mb-2">
            <summary className="small" style={{ cursor: "pointer" }}>Fields you can use (click to add)</summary>
            {Object.entries(groups).map(([g, list]) => (
              <div key={g} className="mt-2">
                <div className="small fw-bold">{g}</div>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {list.map((f) => (
                    <button key={f.key} type="button" title={f.label} onClick={() => addField(f.key)}
                      style={{ border: "1px solid #ddd", background: "#fafafa", borderRadius: 12, padding: "1px 8px", fontSize: 12 }}>
                      {f.key}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </details>
          {msg.text && <div className={`alert ${msg.type === "ok" ? "alert-success" : "alert-danger"} py-2`}>{msg.text}</div>}
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-outline-secondary" disabled={busy || !form.query.trim()} onClick={check}>Test query</button>
            <button type="submit" className="btn btn-purple" disabled={busy}>Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Screens({ user }) {
  const [custom, setCustom] = useState(() => loadUserData("screens", user, []));
  const [showAll, setShowAll] = useState(false);
  const [openScreen, setOpenScreen] = useState(null);
  const [openIndustry, setOpenIndustry] = useState(null);
  const [creating, setCreating] = useState(false);

  const persist = (next) => {
    setCustom(next);
    saveUserData("screens", user, next);
  };

  const createScreen = (s) => {
    persist([...custom, { id: `custom-${Date.now()}`, ...s }]);
    setCreating(false);
  };

  if (openIndustry) {
    return <IndustryCompanies industry={openIndustry} onBack={() => setOpenIndustry(null)} />;
  }

  if (openScreen) {
    const isCustom = custom.some((c) => c.id === openScreen.id);
    return (
      <Results
        key={openScreen.id}
        screen={openScreen}
        isCustom={isCustom}
        onBack={() => setOpenScreen(null)}
        onDelete={() => {
          persist(custom.filter((c) => c.id !== openScreen.id));
          setOpenScreen(null);
        }}
        onSaveQuery={(q) => {
          if (isCustom) {
            persist(custom.map((c) => (c.id === openScreen.id ? { ...c, query: q } : c)));
            return "Saved.";
          }
          persist([...custom, { id: `custom-${Date.now()}`, name: `${openScreen.name} (my version)`, description: openScreen.description, query: q }]);
          return 'Saved under "Your screens".';
        }}
      />
    );
  }

  const popular = showAll ? BUILT_IN : POPULAR_IDS.map((id) => BUILT_IN.find((s) => s.id === id)).filter(Boolean);

  return (
    <main className="page-bg">
      <div className="dash-main-content screens-wrap">
        <div className="screens-layout">
          <div className="screens-main">
            <div className="screens-header">
              <h1 className="page-title">Stock screens</h1>
              <button className="btn btn-purple create-screen-btn" onClick={() => setCreating(true)}>
                CREATE NEW SCREEN
              </button>
            </div>

            {custom.length > 0 && (
              <ScreenSection title="Your screens" subtitle="Screens you created" screens={custom} onOpen={setOpenScreen} />
            )}

            {SCREEN_SECTIONS.map((section) => (
              <ScreenSection key={section.id} title={section.title} subtitle={section.subtitle} screens={section.screens} onOpen={setOpenScreen} />
            ))}

            <ScreenSection
              title="Popular stock screens"
              subtitle="Popular screens commonly used by investors."
              screens={popular}
              onOpen={setOpenScreen}
              footer={
                <div className="screens-footer">
                  <button className="show-all-btn" onClick={() => setShowAll(!showAll)}>
                    {showAll ? "SHOW FEWER" : "SHOW ALL SCREENS"} <ChevronRight size={16} />
                  </button>
                </div>
              }
            />
          </div>

          <BrowseSectors industries={INDUSTRIES} onOpen={setOpenIndustry} />
        </div>
      </div>

      {creating && <CreateModal onSave={createScreen} onClose={() => setCreating(false)} />}
    </main>
  );
}



